import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Button,
  Divider,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Tooltip,
  Skeleton,
  Fade,
  Slide,
  useTheme,
  alpha,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Close as CloseIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Settings as SettingsIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  LocalHospital as MedicalIcon,
  Payment as PaymentIcon,
  Message as MessageIcon,
  Event as EventIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

import { useFeatureFlag } from '../../config/featureFlags';
import { notificationService } from '../../services/notificationService';
import useWebSocket from '../../hooks/useWebSocket';
import { WS_URL } from '../../config/apiConfig';
import { useAuth } from '../../hooks/useAuth';

const NOTIFICATION_TYPES = {
  APPOINTMENT: { icon: EventIcon, color: '#2196f3', label: 'Appointments' },
  PAYMENT: { icon: PaymentIcon, color: '#4caf50', label: 'Payments' },
  MESSAGE: { icon: MessageIcon, color: '#ff9800', label: 'Messages' },
  MEDICAL: { icon: MedicalIcon, color: '#f44336', label: 'Medical' },
  SYSTEM: { icon: InfoIcon, color: '#9c27b0', label: 'System' },
  WARNING: { icon: WarningIcon, color: '#ff5722', label: 'Warnings' },
  SUCCESS: { icon: CheckCircleIcon, color: '#4caf50', label: 'Success' },
  ERROR: { icon: ErrorIcon, color: '#f44336', label: 'Errors' }
};

const ROLE_SPECIFIC_FILTERS = {
  patient: ['APPOINTMENT', 'PAYMENT', 'MESSAGE', 'MEDICAL', 'SYSTEM'],
  doctor: ['APPOINTMENT', 'MESSAGE', 'MEDICAL', 'SYSTEM', 'WARNING'],
  receptionist: ['APPOINTMENT', 'PAYMENT', 'SYSTEM', 'WARNING'],
  admin: ['APPOINTMENT', 'PAYMENT', 'MESSAGE', 'MEDICAL', 'SYSTEM', 'WARNING', 'SUCCESS', 'ERROR']
};

const NotificationCenter = ({ user }) => {
  const theme = useTheme();
  const { user: authUser } = useAuth();
  const currentUser = user || authUser;
  
  // Feature flags
  const notificationsEnabled = useFeatureFlag('FEATURE_NOTIFICATIONS');
  const websocketEnabled = useFeatureFlag('FEATURE_NOTIFICATIONS_WS');

  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState(new Set());
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // WebSocket connection
  const {
    isConnected,
    lastMessage,
    sendMessage,
    connect,
    disconnect
  } = useWebSocket(
    websocketEnabled && currentUser ? `${WS_URL}/ws/notifications/?token=${localStorage.getItem('access_token')}` : null,
    {
      onMessage: handleWebSocketMessage,
      onOpen: handleWebSocketOpen,
      onClose: handleWebSocketClose,
      onError: handleWebSocketError,
      reconnectAttempts: 5,
      reconnectInterval: 3000
    }
  );

  // WebSocket event handlers
  function handleWebSocketMessage(message) {
    console.log('📡 WebSocket message received:', message);
    
    if (message.type === 'notification') {
      const newNotification = message.notification;
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Play notification sound
      playNotificationSound();
      
      // Show toast for new notification
      showNotificationToast(newNotification);
    } else if (message.type === 'unread_count') {
      setUnreadCount(message.count);
    } else if (message.type === 'mark_read_response') {
      if (message.success) {
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === message.notification_id
              ? { ...notif, is_read: true, read_at: new Date().toISOString() }
              : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    }
  }

  function handleWebSocketOpen() {
    console.log('🔗 WebSocket connected');
    setError(null);
  }

  function handleWebSocketClose() {
    console.log('🔌 WebSocket disconnected');
  }

  function handleWebSocketError(error) {
    console.error('❌ WebSocket error:', error);
    setError('Connection lost. Trying to reconnect...');
  }

  // Notification management
  const fetchNotifications = useCallback(async (pageNum = 1, filters = {}) => {
    if (!currentUser) return;

    try {
      setLoading(pageNum === 1);
      const response = await notificationService.getInAppNotifications({
        page: pageNum,
        page_size: 20,
        ...filters
      });

      if (pageNum === 1) {
        setNotifications(response.results || []);
      } else {
        setNotifications(prev => [...prev, ...(response.results || [])]);
      }

      setHasMore(!!response.next);
      setPage(pageNum);
      
      // Update unread count
      const unreadResponse = await notificationService.getUnreadCount();
      setUnreadCount(unreadResponse.count || 0);
      
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentUser]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      if (websocketEnabled && isConnected) {
        sendMessage({
          type: 'mark_read',
          notification_id: notificationId
        });
      } else {
        await notificationService.markAsRead(notificationId);
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId
              ? { ...notif, is_read: true, read_at: new Date().toISOString() }
              : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, [websocketEnabled, isConnected, sendMessage]);

  const markAllAsRead = useCallback(async () => {
    try {
      if (websocketEnabled && isConnected) {
        sendMessage({ type: 'mark_all_read' });
      } else {
        await notificationService.markAllAsRead();
        setNotifications(prev =>
          prev.map(notif => ({ ...notif, is_read: true, read_at: new Date().toISOString() }))
        );
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  }, [websocketEnabled, isConnected, sendMessage]);

  const refreshNotifications = useCallback(() => {
    setRefreshing(true);
    fetchNotifications(1);
  }, [fetchNotifications]);

  // UI helpers
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification-sound.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Ignore audio play errors (user interaction required)
      });
    } catch (err) {
      // Ignore audio errors
    }
  };

  const showNotificationToast = (notification) => {
    // This would integrate with a toast system
    console.log('🔔 New notification:', notification.title);
  };

  const getNotificationIcon = (notification) => {
    const type = notification.type || 'SYSTEM';
    const config = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.SYSTEM;
    const IconComponent = config.icon;
    return <IconComponent sx={{ color: config.color }} />;
  };

  const getNotificationColor = (notification) => {
    const type = notification.type || 'SYSTEM';
    const config = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.SYSTEM;
    return config.color;
  };

  const formatNotificationTime = (timestamp) => {
    try {
      const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (err) {
      return 'Recently';
    }
  };

  // Filter notifications based on selected filters and role
  const filteredNotifications = useMemo(() => {
    let filtered = notifications;

    // Apply role-based filters
    const roleFilters = ROLE_SPECIFIC_FILTERS[currentUser?.role] || ROLE_SPECIFIC_FILTERS.patient;
    filtered = filtered.filter(notif => {
      const type = notif.type || 'SYSTEM';
      return roleFilters.includes(type);
    });

    // Apply user-selected filters
    if (selectedFilters.size > 0) {
      filtered = filtered.filter(notif => {
        const type = notif.type || 'SYSTEM';
        return selectedFilters.has(type);
      });
    }

    // Apply tab filters
    if (selectedTab === 1) {
      filtered = filtered.filter(notif => !notif.is_read);
    } else if (selectedTab === 2) {
      filtered = filtered.filter(notif => notif.is_read);
    }

    return filtered;
  }, [notifications, selectedFilters, selectedTab, currentUser?.role]);

  // Effects
  useEffect(() => {
    if (notificationsEnabled && currentUser) {
      fetchNotifications();
    }
  }, [notificationsEnabled, currentUser, fetchNotifications]);

  useEffect(() => {
    if (websocketEnabled && currentUser && !isConnected) {
      connect();
    }

    return () => {
      if (isConnected) {
        disconnect();
      }
    };
  }, [websocketEnabled, currentUser, connect, disconnect, isConnected]);

  // Don't render if notifications are disabled
  if (!notificationsEnabled) {
    return null;
  }

  return (
    <>
      {/* Notification Bell */}
      <Tooltip title="Notifications" arrow>
        <IconButton
          color="inherit"
          onClick={() => setIsOpen(true)}
          sx={{
            position: 'relative',
            '&:hover': {
              backgroundColor: alpha(theme.palette.common.white, 0.1),
            }
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.1)' },
                  '100%': { transform: 'scale(1)' }
                }
              }
            }}
          >
            {unreadCount > 0 ? (
              <motion.div
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.3 }}
              >
                <NotificationsIcon />
              </motion.div>
            ) : (
              <NotificationsNoneIcon />
            )}
          </Badge>
          
          {/* Connection indicator */}
          {websocketEnabled && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: isConnected ? '#4caf50' : '#f44336',
                border: '1px solid white',
                zIndex: 1
              }}
            />
          )}
        </IconButton>
      </Tooltip>

      {/* Notification Center Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 400, md: 450 },
            maxWidth: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <NotificationsIcon />
              <Typography variant="h6" fontWeight="bold">
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Chip
                  label={unreadCount}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.2),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              )}
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              {/* Refresh button */}
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  onClick={refreshNotifications}
                  disabled={refreshing}
                  sx={{ color: 'white' }}
                >
                  {refreshing ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>

              {/* Filter button */}
              <Tooltip title="Filter">
                <IconButton
                  size="small"
                  onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                  sx={{ color: 'white' }}
                >
                  <FilterIcon />
                </IconButton>
              </Tooltip>

              {/* Settings button */}
              <Tooltip title="Settings">
                <IconButton
                  size="small"
                  sx={{ color: 'white' }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>

              {/* Close button */}
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Connection status */}
          {websocketEnabled && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: isConnected ? '#4caf50' : '#f44336'
                }}
              />
              <Typography variant="caption">
                {isConnected ? 'Real-time updates active' : 'Offline mode'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="fullWidth"
          >
            <Tab label={`All (${notifications.length})`} />
            <Tab label={`Unread (${unreadCount})`} />
            <Tab label="Read" />
          </Tabs>
        </Box>

        {/* Quick Actions */}
        {unreadCount > 0 && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<MarkEmailReadIcon />}
              onClick={markAllAsRead}
              size="small"
            >
              Mark All as Read
            </Button>
          </Box>
        )}

        {/* Error display */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Notifications List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading && page === 1 ? (
            // Loading skeletons
            <Box sx={{ p: 2 }}>
              {[...Array(5)].map((_, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box display="flex" gap={2}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="40%" />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : filteredNotifications.length === 0 ? (
            // Empty state
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 4,
                textAlign: 'center'
              }}
            >
              <NotificationsNoneIcon
                sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedTab === 1
                  ? "You're all caught up!"
                  : "Notifications will appear here when you receive them."}
              </Typography>
            </Box>
          ) : (
            // Notifications list
            <List sx={{ p: 0 }}>
              <AnimatePresence>
                {filteredNotifications.map((notification, index) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <NotificationItem
                      notification={notification}
                      onMarkAsRead={markAsRead}
                      onClick={() => {
                        if (!notification.is_read) {
                          markAsRead(notification.id);
                        }
                        if (notification.link_url) {
                          window.location.href = notification.link_url;
                        }
                      }}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Load more button */}
              {hasMore && !loading && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => fetchNotifications(page + 1)}
                    disabled={loading}
                  >
                    Load More
                  </Button>
                </Box>
              )}

              {/* Loading more indicator */}
              {loading && page > 1 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        PaperProps={{
          sx: { minWidth: 200 }
        }}
      >
        <MenuItem dense>
          <Typography variant="subtitle2" color="text.secondary">
            Filter by type:
          </Typography>
        </MenuItem>
        <Divider />
        {Object.entries(NOTIFICATION_TYPES).map(([type, config]) => {
          const roleFilters = ROLE_SPECIFIC_FILTERS[currentUser?.role] || [];
          if (!roleFilters.includes(type)) return null;

          const IconComponent = config.icon;
          const isSelected = selectedFilters.has(type);

          return (
            <MenuItem
              key={type}
              dense
              onClick={() => {
                const newFilters = new Set(selectedFilters);
                if (isSelected) {
                  newFilters.delete(type);
                } else {
                  newFilters.add(type);
                }
                setSelectedFilters(newFilters);
              }}
            >
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <IconComponent sx={{ color: config.color, fontSize: 20 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {config.label}
                </Typography>
                {isSelected && <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
              </Box>
            </MenuItem>
          );
        })}
        
        {selectedFilters.size > 0 && (
          <>
            <Divider />
            <MenuItem
              dense
              onClick={() => setSelectedFilters(new Set())}
            >
              <Typography variant="body2" color="primary">
                Clear filters
              </Typography>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

// Individual notification item component
const NotificationItem = ({ notification, onMarkAsRead, onClick }) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const getNotificationIcon = () => {
    const type = notification.type || 'SYSTEM';
    const config = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.SYSTEM;
    const IconComponent = config.icon;
    return <IconComponent sx={{ color: config.color }} />;
  };

  const formatTime = (timestamp) => {
    try {
      const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (err) {
      return 'Recently';
    }
  };

  return (
    <ListItem
      button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        borderLeft: notification.is_read ? 'none' : `4px solid ${theme.palette.primary.main}`,
        backgroundColor: notification.is_read
          ? 'transparent'
          : alpha(theme.palette.primary.main, 0.05),
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        },
        transition: 'all 0.2s ease-in-out',
        position: 'relative'
      }}
    >
      <ListItemAvatar>
        <Avatar
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            border: `2px solid ${theme.palette.primary.main}`,
          }}
        >
          {getNotificationIcon()}
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        primary={
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: notification.is_read ? 'normal' : 'bold',
                color: notification.is_read ? 'text.secondary' : 'text.primary'
              }}
            >
              {notification.title}
            </Typography>
            {!notification.is_read && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  ml: 1
                }}
              />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 0.5
              }}
            >
              {notification.body}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTime(notification.created_at)}
            </Typography>
          </Box>
        }
      />

      {/* Quick actions on hover */}
      <Fade in={isHovered && !notification.is_read}>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1
          }}
        >
          <Tooltip title="Mark as read">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead(notification.id);
              }}
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                '&:hover': {
                  backgroundColor: theme.palette.background.paper,
                }
              }}
            >
              <MarkEmailReadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Fade>
    </ListItem>
  );
};

export default NotificationCenter;
